import type { PlanStop, PlanTruck } from "./api";
import { stopNumbers } from "./routePlan";
import { stopGal, type TruckLoad, truckLoads } from "./truckLoad";

/** The service minutes of the planner, as in `Costs` of platform/api/app/planner/plan.py (rule V-7). */
export const SERVICE = { fixedMin: 5, galPerMin: 40, emptyCheckMin: 4, dumpMin: 20 } as const;

export const TICK_MIN = 15;
export const LABEL_MIN = 30;
export const MIN_BOX_PX = 22;
export const LEFT_PX = 200;
export const PHONE_LEFT_PX = 118;
export const MIN_PX_PER_MIN = 4;
export const ICON_W = 30;
export const LANE_GAP_PX = 2;
export const TIMELINE_PARAM = "timeline";

/** Minutes after midnight of "07:05", or null. */
export function clockMinutes(text: string | null | undefined): number | null {
  const found = /^(\d{1,2}):(\d{2})/.exec(text ?? "");
  return found ? Number(found[1]) * 60 + Number(found[2]) : null;
}

/** The minutes at a stop: the planner value, else the planner formula for an older API or plan. */
export function serviceMin(stop: Pick<PlanStop, "kind" | "expected_gal" | "service_min">): number {
  if (stop.service_min != null) return stop.service_min;
  if (stop.kind === "dump") return SERVICE.dumpMin;
  const gal = stop.expected_gal ?? 0;
  return gal >= 1 ? SERVICE.fixedMin + gal / SERVICE.galPerMin : SERVICE.emptyCheckMin;
}

export interface TimelineBox {
  at: number;
  seq: number;
  kind: "stop" | "dump";
  number: number | null;
  start: number;
  minutes: number;
  /** No oil is expected: the driver only checks the container. */
  empty: boolean;
  load: TruckLoad | null;
}

export interface TimelineRow {
  truck: string;
  start: number | null;
  end: number | null;
  boxes: TimelineBox[];
}

/** The boxes of a truck: each stop and dump at its arrival, with its minutes and the truck load after it. */
export function timelineRow(truck: Pick<PlanTruck, "truck" | "start" | "end" | "capacity" | "stops">): TimelineRow {
  const loads = truckLoads(truck);
  const numbers = stopNumbers(truck.stops);
  const boxes: TimelineBox[] = [];
  truck.stops.forEach((stop, at) => {
    const start = clockMinutes(stop.arrive);
    if (start === null) return;
    const empty = stop.kind === "stop" && stopGal(stop) < 1;
    boxes.push({ at, seq: stop.seq, kind: stop.kind, number: numbers[at], start, minutes: serviceMin(stop), empty, load: loads[at] });
  });
  return { truck: truck.truck, start: truck.start, end: truck.end, boxes };
}

/** The span of the axis: the earliest shift start to the latest end, on whole half hours. */
export function timelineSpan(rows: TimelineRow[]): { from: number; to: number } | null {
  const times = rows.flatMap((row) => [row.start, row.end, ...row.boxes.flatMap((box) => [box.start, box.start + box.minutes])]).filter((t): t is number => t !== null);
  if (!times.length) return null;
  const from = Math.floor(Math.min(...times) / LABEL_MIN) * LABEL_MIN;
  const to = Math.max(from + LABEL_MIN, Math.ceil(Math.max(...times) / LABEL_MIN) * LABEL_MIN);
  return { from, to };
}

/** The width of the truck column: narrower in a card of a phone. */
export const leftPx = (width: number) => (width < 600 ? PHONE_LEFT_PX : LEFT_PX);

/** The pixels for each minute: the chart fills the card, and scrolls when a minute gets too narrow. */
export const pxPerMin = (width: number, span: { from: number; to: number }, left = leftPx(width)) => Math.max(MIN_PX_PER_MIN, (width - left - 16) / (span.to - span.from));

export const xOf = (minutes: number, from: number, px: number, left = LEFT_PX) => left + (minutes - from) * px;

export const boxWidth = (minutes: number, px: number) => Math.max(minutes * px, MIN_BOX_PX);

export interface PlacedBox {
  box: TimelineBox;
  left: number;
  width: number;
  /** 0 is on the shift line; each lane above it holds a box that would cover a box below. */
  lane: number;
  /** The left and right pixels of the box and of its truck icon together. */
  from: number;
  to: number;
}

/** Each box at its pixels, in the lowest lane where the box and its truck icon cover no other box or icon. */
export function placeBoxes(boxes: TimelineBox[], px: number, x: (minutes: number) => number): { placed: PlacedBox[]; lanes: number } {
  const ends: number[] = [];
  const placed = [...boxes]
    .sort((a, b) => a.start - b.start || a.seq - b.seq)
    .map((box) => {
      const left = x(box.start);
      const width = boxWidth(box.minutes, px);
      const middle = left + width / 2;
      const from = box.load ? Math.min(left, middle - ICON_W / 2) : left;
      const to = box.load ? Math.max(left + width, middle + ICON_W / 2) : left + width;
      let lane = ends.findIndex((end) => end + LANE_GAP_PX <= from);
      if (lane < 0) lane = ends.length;
      ends[lane] = to;
      return { box, left, width, lane, from, to };
    });
  return { placed, lanes: Math.max(1, ends.length) };
}

/** A tick each 15 minutes, with a label each 30 minutes. */
export function timelineTicks(span: { from: number; to: number }): { minutes: number; label: boolean }[] {
  const ticks = [];
  for (let m = Math.ceil(span.from / TICK_MIN) * TICK_MIN; m <= span.to; m += TICK_MIN) ticks.push({ minutes: m, label: m % LABEL_MIN === 0 });
  return ticks;
}

/** "7:30 AM" or "07:30", in the time format of the locale. */
export function axisText(minutes: number, locale?: string): string {
  const at = new Date(Date.UTC(2000, 0, 1, Math.floor(minutes / 60) % 24, minutes % 60));
  return at.toLocaleTimeString(locale, { timeZone: "UTC", hour: "numeric", minute: "2-digit" });
}

/** The minutes after midnight now in the time zone, for the Now line. */
export function nowMinutes(now: Date, timeZone?: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return part("hour") * 60 + part("minute");
}

/** "About 11 min at the stop". */
export const minutesAtText = (box: Pick<TimelineBox, "kind" | "minutes">) => `about ${Math.round(box.minutes)} min ${box.kind === "dump" ? "at the depot" : "at the stop"}`;

/** The timeline is open unless the URL says `timeline=0`, as the map is (rule U-23). */
export const timelineOpenOf = (search: string) => new URLSearchParams(search).get(TIMELINE_PARAM) !== "0";

export function withTimelineOpen(search: string, open: boolean): string {
  const params = new URLSearchParams(search);
  if (open) params.delete(TIMELINE_PARAM);
  else params.set(TIMELINE_PARAM, "0");
  const text = params.toString();
  return text ? `?${text}` : "";
}
